/**
 * VerificationPanel — PM review panel for role completion verification.
 *
 * Shows all assignments in `pending_verification` or `pending_escalated_review`
 * status. PM can:
 *   - One-click confirm (quick_confirm tier) or full confirm (standard/evidence tiers)
 *   - Reject (reverts to active)
 *   - View attached evidence for evidence_required tier
 *
 * Escalated items are flagged so the PM knows they've been forwarded to the
 * Community Leader (handled by the workflow engine, not this panel).
 */

import React, { useMemo, useCallback, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { updateRoleAssignment } from "../../api/projectRoles";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
} from "../../api/projectRoles";
import type { SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./VerificationPanel.module.css";

interface Props {
  projectGuid: string;
  slots: ProjectRoleSlotResponse[];
  userMap: Map<string, SecurityUserResponse>;
  onActionComplete: () => void;
}

interface VerificationItem {
  slot: ProjectRoleSlotResponse;
  assignment: ProjectRoleAssignmentResponse;
  isEscalated: boolean;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const VERIFICATION_LABELS: Record<string, string> = {
  auto: "Auto",
  quick_confirm: "Quick Confirm",
  standard: "Standard",
  evidence_required: "Evidence Required",
};

const VerificationPanel: React.FC<Props> = ({
  projectGuid,
  slots,
  userMap,
  onActionComplete,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedEvidence, setExpandedEvidence] = useState<Set<string>>(new Set());

  const items: VerificationItem[] = useMemo(() => {
    const result: VerificationItem[] = [];
    for (const slot of slots) {
      for (const a of slot.assignments || []) {
        if (a.status === "pending_verification") {
          result.push({ slot, assignment: a, isEscalated: false });
        } else if (a.status === "pending_escalated_review") {
          result.push({ slot, assignment: a, isEscalated: true });
        }
      }
    }
    // Sort newest first
    result.sort((a, b) =>
      new Date(b.assignment.assignedAt).getTime() - new Date(a.assignment.assignedAt).getTime()
    );
    return result;
  }, [slots]);

  const handleConfirm = useCallback(
    async (item: VerificationItem) => {
      setProcessingId(item.assignment.assignmentGuid);
      try {
        await updateRoleAssignment(
          projectGuid,
          item.slot.roleSlotGuid,
          item.assignment.assignmentGuid,
          { status: "completed" }
        );
        onActionComplete();
      } catch (err) {
        console.error("[VerificationPanel] Confirm failed:", err);
      } finally {
        setProcessingId(null);
      }
    },
    [projectGuid, onActionComplete]
  );

  const handleReject = useCallback(
    async (item: VerificationItem) => {
      setProcessingId(item.assignment.assignmentGuid);
      try {
        // Rejecting a verification reverts to active (back to the volunteer)
        await updateRoleAssignment(
          projectGuid,
          item.slot.roleSlotGuid,
          item.assignment.assignmentGuid,
          { status: "active" }
        );
        onActionComplete();
      } catch (err) {
        console.error("[VerificationPanel] Reject failed:", err);
      } finally {
        setProcessingId(null);
      }
    },
    [projectGuid, onActionComplete]
  );

  const expandEvidence = useCallback((guid: string) => {
    setExpandedEvidence((prev) => {
      if (prev.has(guid)) return prev;
      const next = new Set(prev);
      next.add(guid);
      return next;
    });
  }, []);

  const collapseEvidence = useCallback((guid: string) => {
    setExpandedEvidence((prev) => {
      if (!prev.has(guid)) return prev;
      const next = new Set(prev);
      next.delete(guid);
      return next;
    });
  }, []);

  if (items.length === 0) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>{t("verification.title")}</span>
        <span className={styles.badge}>{items.length}</span>
      </div>
      <div className={styles.list}>
        {items.map((item) => {
          const user = userMap.get(item.assignment.userGuid.toUpperCase());
          const isProcessing = processingId === item.assignment.assignmentGuid;
          const hasEvidence =
            (item.assignment.evidenceUrls && item.assignment.evidenceUrls.length > 0) ||
            !!item.assignment.evidenceNotes;
          const showEvidence = expandedEvidence.has(item.assignment.assignmentGuid);
          const verLevel = item.slot.verificationLevel || "auto";

          return (
            <div key={item.assignment.assignmentGuid} className={styles.item}>
              <div className={styles.itemMain}>
                {/* Avatar */}
                <div className={styles.avatar}>
                  {user && profileImgUrl(user) ? (
                    <img src={profileImgUrl(user)!} alt="" className={styles.avatarImg} />
                  ) : (
                    <span className={styles.avatarInitials}>
                      {user ? getInitials(user.displayName || user.name) : "??"}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className={styles.info}>
                  <div className={styles.userName}>
                    {user?.displayName || user?.name || t("verification.unknownUser")}
                  </div>
                  <div className={styles.roleName}>
                    {item.slot.roleLabel}
                    <span className={styles.verBadge}>
                      {VERIFICATION_LABELS[verLevel] || verLevel}
                    </span>
                    {item.isEscalated && (
                      <span className={styles.escalatedBadge}>
                        {t("verification.escalated")}
                      </span>
                    )}
                  </div>
                  {item.assignment.instructions && (
                    <div className={styles.instructions}>
                      {item.assignment.instructions}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                  {hasEvidence && (
                    <button
                      type="button"
                      className={styles.evidenceBtn}
                      onClick={() => showEvidence ? collapseEvidence(item.assignment.assignmentGuid) : expandEvidence(item.assignment.assignmentGuid)}
                    >
                      {showEvidence
                        ? t("verification.hideEvidence")
                        : t("verification.viewEvidence")}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.confirmBtn}
                    onClick={() => handleConfirm(item)}
                    disabled={isProcessing}
                  >
                    {t("verification.confirm")}
                  </button>
                  <button
                    type="button"
                    className={styles.rejectBtn}
                    onClick={() => handleReject(item)}
                    disabled={isProcessing}
                  >
                    {t("verification.reject")}
                  </button>
                </div>
              </div>

              {/* Evidence drawer */}
              {showEvidence && hasEvidence && (
                <div className={styles.evidenceDrawer}>
                  {item.assignment.evidenceNotes && (
                    <div className={styles.evidenceNotes}>
                      {item.assignment.evidenceNotes}
                    </div>
                  )}
                  {item.assignment.evidenceUrls && item.assignment.evidenceUrls.length > 0 && (
                    <div className={styles.evidenceLinks}>
                      {item.assignment.evidenceUrls.map((url, idx) => (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.evidenceLink}
                        >
                          {t("verification.evidenceItem", { n: idx + 1 })}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VerificationPanel;
